import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CalendarClock,
  Clock,
  ExternalLink,
  MapPin,
  Search,
  StickyNote,
  UserRound,
  X,
} from 'lucide-react'
import { addDays, format } from 'date-fns'

import { Avatar } from '@/components/common/Avatar'
import { PageShell } from '@/components/common/PageShell'
import { Badge, Button, Card, Input } from '@/components/ui'
import { SectionLoader } from '@/components/common/SectionLoader'
import { usePermissions } from '@/hooks/usePermissions'
import { useCompanies, useMyCompanies } from '@/features/companies/hooks/useCompanies'
import { cn } from '@/lib/cn'
import type {
  ShiftAssignment,
  ShiftAssignmentShiftRef,
  ShiftAssignmentUserRef,
} from '@/types/shift.types'
import {
  useShiftAssignmentsAcrossCompanies,
  type CompanyScopedAssignment,
} from '../hooks/useShiftsAcrossCompanies'
import { getAssignmentShiftName } from '../utils/assignmentUtils'
import { getCompanyColor } from '../utils/companyColor'
import { QueryErrorState } from '@/components/common/QueryErrorState'

type WindowKey = '24h' | '3d' | '5d' | '7d'

const WINDOW_OPTIONS: Array<{ key: WindowKey; label: string; days: number; headline: string }> = [
  { key: '24h', label: '24h', days: 1, headline: 'Next 24 hours' },
  { key: '3d', label: '3 days', days: 3, headline: 'Next 3 days' },
  { key: '5d', label: '5 days', days: 5, headline: 'Next 5 days' },
  { key: '7d', label: '7 days', days: 7, headline: 'Next 7 days' },
]

interface UpcomingShiftItem {
  assignment: CompanyScopedAssignment
  startsAt: Date
  endsAt: Date | null
  companyName: string
  userName: string
  userEmail?: string
  shiftName: string
  startTime: string
  endTime: string
}

function dateKey(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

function getShiftRef(assignment: ShiftAssignment): ShiftAssignmentShiftRef | undefined {
  if (assignment.shift) return assignment.shift
  if (typeof assignment.shiftId === 'object' && assignment.shiftId && 'startTime' in assignment.shiftId) {
    return assignment.shiftId
  }
  return undefined
}

function getTimeRange(assignment: ShiftAssignment): { startTime: string; endTime: string } {
  const shift = getShiftRef(assignment)
  return {
    startTime: assignment.startTime || shift?.startTime || '',
    endTime: assignment.endTime || shift?.endTime || '',
  }
}

function combineLocalDateTime(day: string, time: string): Date | null {
  if (!day || !time) return null
  const normalizedDay = day.slice(0, 10)
  const normalizedTime = time.length === 5 ? `${time}:00` : time
  const date = new Date(`${normalizedDay}T${normalizedTime}`)
  return Number.isNaN(date.getTime()) ? null : date
}

function getEndDate(start: Date, day: string, startTime: string, endTime: string): Date | null {
  const end = combineLocalDateTime(day, endTime)
  if (!end) return null
  if (startTime && endTime && end <= start) {
    end.setDate(end.getDate() + 1)
  }
  return end
}

function getUserRef(assignment: ShiftAssignment): ShiftAssignmentUserRef | undefined {
  if (assignment.user) return assignment.user
  return typeof assignment.userId === 'object' ? assignment.userId : undefined
}

function getUserName(assignment: ShiftAssignment): { name: string; email?: string } {
  const ref = getUserRef(assignment)
  const fallbackId = typeof assignment.userId === 'string' ? assignment.userId : assignment.userId._id
  const name =
    ref &&
    ([ref.firstName, ref.lastName].filter(Boolean).join(' ').trim() || ref.email)
  return {
    name: name || fallbackId,
    email: ref?.email,
  }
}

function statusVariant(status: ShiftAssignment['status']): 'success' | 'danger' | 'info' {
  if (status === 'confirmed') return 'success'
  if (status === 'cancelled') return 'danger'
  return 'info'
}

function formatDateLabel(date: Date): string {
  const today = dateKey(new Date())
  const tomorrow = dateKey(addDays(new Date(), 1))
  const key = dateKey(date)
  if (key === today) return `Today, ${format(date, 'MMM d')}`
  if (key === tomorrow) return `Tomorrow, ${format(date, 'MMM d')}`
  return format(date, 'EEE, MMM d')
}

function getHourKey(date: Date): string {
  return format(date, 'yyyy-MM-dd-HH')
}

function formatHourLabel(date: Date): string {
  return format(date, 'h a')
}

function formatHourDateLabel(date: Date): string {
  const today = dateKey(new Date())
  const tomorrow = dateKey(addDays(new Date(), 1))
  const key = dateKey(date)
  if (key === today) return 'Today'
  if (key === tomorrow) return 'Tomorrow'
  return format(date, 'MMM d')
}

function groupItemsByCompany(items: UpcomingShiftItem[]): Array<{
  companyName: string
  companyId: string
  items: UpcomingShiftItem[]
}> {
  const groups = new Map<string, { companyName: string; companyId: string; items: UpcomingShiftItem[] }>()
  items.forEach((item) => {
    const companyId = item.assignment.companyId ?? item.companyName
    const key = `${companyId}:${item.companyName}`
    const existing = groups.get(key)
    if (existing) {
      existing.items.push(item)
      return
    }
    groups.set(key, { companyName: item.companyName, companyId, items: [item] })
  })
  return Array.from(groups.values()).sort((a, b) => a.companyName.localeCompare(b.companyName))
}

function buildUpcomingItems(input: {
  assignments: CompanyScopedAssignment[]
  companyNameById: Map<string, string>
  search: string
  statusFilter: string
  now: Date
  days: number
}): UpcomingShiftItem[] {
  const query = input.search.trim().toLowerCase()
  const upperBound = new Date(input.now)
  upperBound.setHours(upperBound.getHours() + input.days * 24)

  return input.assignments
    .filter((assignment) => input.statusFilter === 'cancelled' || assignment.status !== 'cancelled')
    .map((assignment): UpcomingShiftItem | null => {
      const { startTime, endTime } = getTimeRange(assignment)
      const startsAt = combineLocalDateTime(assignment.date, startTime)
      if (!startsAt || startsAt < input.now || startsAt > upperBound) return null

      const companyName = input.companyNameById.get(assignment.companyId ?? '') ?? 'Unknown company'
      const user = getUserName(assignment)
      const shiftName = getAssignmentShiftName(assignment)
      if (query && !user.name.toLowerCase().includes(query)) return null

      return {
        assignment,
        startsAt,
        endsAt: getEndDate(startsAt, assignment.date, startTime, endTime),
        companyName,
        userName: user.name,
        userEmail: user.email,
        shiftName,
        startTime,
        endTime,
      }
    })
    .filter((item): item is UpcomingShiftItem => item !== null)
    .sort((a, b) => {
      const time = a.startsAt.getTime() - b.startsAt.getTime()
      if (time !== 0) return time
      const company = a.companyName.localeCompare(b.companyName)
      if (company !== 0) return company
      return a.userName.localeCompare(b.userName)
    })
}

function buildShiftSearchParams(input: {
  companyId: string | null
  status: string
  search: string
  assignment?: CompanyScopedAssignment
}): string {
  const params = new URLSearchParams()
  const companyId = input.assignment?.companyId ?? input.companyId
  if (companyId) params.set('companyId', companyId)
  if (input.status) params.set('status', input.status)
  if (input.search) params.set('search', input.search)
  if (input.assignment?._id) params.set('shiftAssignmentId', input.assignment._id)
  return params.toString()
}

export function UpcomingShiftsPage(): React.ReactElement {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { can } = usePermissions()
  const hasShiftsRead = can('shifts', 'read')
  const canReadAllCompanies = can('companies', 'read')
  const allCompaniesQuery = useCompanies(undefined, { enabled: canReadAllCompanies })
  const myCompaniesQuery = useMyCompanies({ enabled: !canReadAllCompanies })
  const companies = useMemo(
    () => (canReadAllCompanies ? (allCompaniesQuery.data ?? []) : (myCompaniesQuery.data ?? [])),
    [allCompaniesQuery.data, canReadAllCompanies, myCompaniesQuery.data]
  )
  const companiesLoading = canReadAllCompanies ? allCompaniesQuery.isLoading : myCompaniesQuery.isLoading

  const selectedCompanyId = searchParams.get('companyId')
  const statusFilter = searchParams.get('status') ?? ''
  const search = searchParams.get('search') ?? ''
  const [activeWindow, setActiveWindow] = useState<WindowKey>('24h')
  const [selectedHourKey, setSelectedHourKey] = useState<string | null>(null)
  const [selectedItem, setSelectedItem] = useState<UpcomingShiftItem | null>(null)

  const companyNameById = useMemo(() => {
    const map = new Map<string, string>()
    companies.forEach((company) => map.set(company._id, company.name))
    return map
  }, [companies])

  const companyIds = selectedCompanyId ? [selectedCompanyId] : []
  const now = useMemo(() => new Date(), [])
  const today = dateKey(now)
  const endDate = dateKey(addDays(now, 7))

  const { assignments, isLoading, isError, error, refetch } = useShiftAssignmentsAcrossCompanies(
    companyIds,
    {
      startDate: today,
      endDate,
      limit: 100,
      ...(statusFilter ? { status: statusFilter } : {}),
    },
    {
      mineOnly: !hasShiftsRead,
      allowAllCompanies: hasShiftsRead && companyIds.length === 0,
    }
  )

  const itemsByWindow = useMemo(() => {
    const entries = WINDOW_OPTIONS.map((option) => [
      option.key,
      buildUpcomingItems({
        assignments,
        companyNameById,
        search,
        statusFilter,
        now,
        days: option.days,
      }),
    ] as const)
    return new Map<WindowKey, UpcomingShiftItem[]>(entries)
  }, [assignments, companyNameById, now, search, statusFilter])

  const activeOption = WINDOW_OPTIONS.find((option) => option.key === activeWindow) ?? WINDOW_OPTIONS[0]
  const activeItems = itemsByWindow.get(activeWindow) ?? []
  const hourBuckets = new Map<string, { key: string; startsAt: Date; count: number }>()
  activeItems.forEach((item) => {
    const key = getHourKey(item.startsAt)
    const current = hourBuckets.get(key)
    if (current) {
      current.count += 1
      return
    }
    hourBuckets.set(key, { key, startsAt: item.startsAt, count: 1 })
  })
  const hourBucketList = Array.from(hourBuckets.values()).sort(
    (a, b) => a.startsAt.getTime() - b.startsAt.getTime()
  )
  const selectedHour = selectedHourKey
    ? hourBucketList.find((bucket) => bucket.key === selectedHourKey)
    : null
  const visibleItems = selectedHourKey
    ? activeItems.filter((item) => getHourKey(item.startsAt) === selectedHourKey)
    : activeItems
  const groupedItems = new Map<string, UpcomingShiftItem[]>()
  visibleItems.forEach((item) => {
    const key = getHourKey(item.startsAt)
    if (!groupedItems.has(key)) groupedItems.set(key, [])
    groupedItems.get(key)!.push(item)
  })

  const updateSearch = (nextSearch: string): void => {
    const next = new URLSearchParams(searchParams)
    if (nextSearch.trim()) next.set('search', nextSearch.trim())
    else next.delete('search')
    setSearchParams(next, { replace: true })
  }

  const schedulePath = (assignment?: CompanyScopedAssignment): string => {
    const query = buildShiftSearchParams({
      companyId: selectedCompanyId,
      status: statusFilter,
      search,
      assignment,
    })
    return query ? `/shifts?${query}` : '/shifts'
  }

  const renderCard = (item: UpcomingShiftItem, prominent = false): React.ReactElement => {
    const color = getCompanyColor(item.assignment.companyId ?? item.companyName)
    const timeLabel =
      item.startTime && item.endTime
        ? `${item.startTime} - ${item.endTime}`
        : format(item.startsAt, 'p')

    return (
      <article
        key={item.assignment._id}
        className={cn(
          'rounded-lg border bg-white p-4 shadow-sm transition hover:border-primary/30 hover:shadow-md dark:bg-gray-900 dark:hover:border-primary-light/40',
          prominent
            ? 'border-primary/30 bg-primary/5 dark:border-primary-light/30 dark:bg-primary-light/20'
            : 'border-gray-200 dark:border-gray-700'
        )}
      >
        <div className="grid min-w-0 gap-4 sm:grid-cols-[minmax(0,1fr)_auto]">
          <div className="flex min-w-0 items-start gap-3">
            <Avatar name={item.userName} size={prominent ? 'lg' : 'md'} />
            <div className="min-w-0 flex-1">
              <h3 className="truncate text-sm font-semibold text-gray-950 dark:text-gray-100">
                {item.shiftName}
                {activeItems[0]?.assignment._id === item.assignment._id ? (
                  <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-primary dark:bg-primary-light/20 dark:text-primary-light">
                    Next
                  </span>
                ) : null}
              </h3>
              <p className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                <UserRound className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span className="truncate">{item.userName}</span>
              </p>
              <div className="mt-3 grid gap-2 text-xs text-gray-600 dark:text-gray-400 md:grid-cols-2">
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="truncate">{format(item.startsAt, 'EEE, MMM d')} · {timeLabel}</span>
                </span>
                <span className="inline-flex min-w-0 items-center gap-1.5">
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', color.dotBg)} aria-hidden />
                  <span className="truncate">{item.companyName}</span>
                </span>
              </div>
              {item.assignment.notes ? (
                <p className="mt-3 flex min-w-0 items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span className="line-clamp-2">{item.assignment.notes}</span>
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-3 dark:border-gray-800 sm:min-w-[132px] sm:flex-col sm:items-end sm:justify-between sm:border-t-0 sm:pt-0">
            <Badge variant={statusVariant(item.assignment.status)} className="capitalize">
              {item.assignment.status}
            </Badge>
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-primary/30 bg-primary/10 px-3 text-xs font-semibold text-primary shadow-sm transition hover:border-primary/30 hover:bg-primary/15 hover:shadow dark:border-primary-light/30 dark:bg-primary-light/10 dark:text-primary-light dark:hover:bg-primary-light/20"
              onClick={() => setSelectedItem(item)}
            >
              Details
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        </div>
      </article>
    )
  }

  const renderCompanyGroups = (
    hourKey: string,
    group: UpcomingShiftItem[]
  ): React.ReactElement => {
    const companyGroups = groupItemsByCompany(group)
    const showCompanyGroup = companyGroups.length > 1

    return (
      <div className="space-y-4">
        {companyGroups.map((companyGroup) => {
          const color = getCompanyColor(companyGroup.companyId)
          return (
            <div key={`${hourKey}:${companyGroup.companyId}`} className="space-y-2">
              {showCompanyGroup ? (
                <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-950">
                  <div className="flex min-w-0 items-center gap-2">
                    <span
                      className={cn('h-2.5 w-2.5 shrink-0 rounded-full', color.dotBg)}
                      aria-hidden
                    />
                    <span className="truncate text-xs font-bold text-gray-800 dark:text-gray-100">
                      {companyGroup.companyName}
                    </span>
                  </div>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                    {companyGroup.items.length}
                  </span>
                </div>
              ) : null}
              <div className="grid gap-3 xl:grid-cols-2">
                {companyGroup.items.map((item) =>
                  renderCard(
                    item,
                    activeItems[0]?.assignment._id === item.assignment._id
                  )
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Time & Attendance', to: '/dashboard' }, { label: 'Shifts', to: '/shifts' }, { label: 'Upcoming' }]}
      icon={CalendarClock}
      title="Upcoming shifts"
      description="A time-based view of assigned shifts starting soon across your workspace."
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="secondary" size="sm" className="gap-1.5" onClick={() => navigate(schedulePath())}>
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
            Back to schedule
          </Button>
          <div className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:border-gray-700 dark:text-gray-300">
            <Building2 className="h-3.5 w-3.5" aria-hidden />
            {selectedCompanyId
              ? (companyNameById.get(selectedCompanyId) ?? 'Selected company')
              : canReadAllCompanies
                ? 'All companies'
                : `${companies.length} companies`}
          </div>
        </div>

        <Card className="overflow-hidden" contentClassName="p-0">
          <div className="border-b border-gray-200 p-4 dark:border-gray-700">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-primary dark:text-primary-light">
                  <CalendarClock className="h-4 w-4" aria-hidden />
                  {activeOption.headline}
                </p>
                <h2 className="mt-1 text-xl font-semibold text-gray-950 dark:text-gray-100">
                  {activeItems.length} assigned {activeItems.length === 1 ? 'shift' : 'shifts'}
                </h2>
              </div>
              <div className="relative w-full sm:w-80">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={search}
                  onChange={(event) => updateSearch(event.target.value)}
                  placeholder="Search staff..."
                  aria-label="Search upcoming shifts by staff"
                  className="h-9 pl-9 pr-9"
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => updateSearch('')}
                    className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-700 dark:bg-gray-900 sm:grid-cols-4">
              {WINDOW_OPTIONS.map((option) => {
                const count = itemsByWindow.get(option.key)?.length ?? 0
                const active = activeWindow === option.key
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setActiveWindow(option.key)
                      setSelectedHourKey(null)
                    }}
                    className={cn(
                      'flex min-h-16 items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition',
                      active
                        ? 'border-primary/30 bg-white text-primary shadow-md ring-1 ring-primary/15 dark:border-primary-light/40 dark:bg-gray-800 dark:text-primary-light dark:ring-primary-light/20'
                        : 'border-transparent bg-transparent text-gray-700 hover:bg-white hover:shadow-sm dark:text-gray-300 dark:hover:bg-gray-800'
                    )}
                    aria-pressed={active}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-bold leading-tight">{option.label}</span>
                      <span className={cn('mt-1 block text-[11px] font-medium', active ? 'text-primary dark:text-primary-light' : 'text-gray-500 dark:text-gray-400')}>
                        {option.headline}
                      </span>
                    </span>
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-2.5 py-1 text-xs font-bold',
                        active
                          ? 'bg-primary/15 text-primary dark:bg-primary-light/20 dark:text-primary-light'
                          : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                      )}
                    >
                      {count}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>

          <div className="p-4">
            {companiesLoading || isLoading ? (
              <SectionLoader variant="list" rows={6} label="Loading upcoming shifts" />
            ) : isError ? (
              <QueryErrorState
                error={error}
                title="Couldn't load upcoming shifts"
                onRetry={refetch}
              />
            ) : activeItems.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50 py-16 text-center dark:border-gray-700 dark:bg-gray-900">
                <CalendarClock className="mx-auto h-9 w-9 text-gray-400" aria-hidden />
                <p className="mt-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  No assigned shifts in this window
                </p>
              </div>
            ) : (
              <div className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
                <aside className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                  <button
                    type="button"
                    onClick={() => setSelectedHourKey(null)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-lg px-4 py-3 text-left transition-colors',
                      selectedHourKey === null
                        ? 'bg-white text-primary shadow-md ring-1 ring-primary/25 dark:bg-gray-800 dark:ring-primary-light/30'
                        : 'text-gray-600 hover:bg-white hover:shadow-sm dark:text-gray-300 dark:hover:bg-gray-800'
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-bold">All hours</span>
                      <span className="mt-0.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                        Full upcoming list
                      </span>
                    </span>
                    <span className="rounded-full bg-primary/15 px-2.5 py-1 text-xs font-bold text-primary dark:bg-primary-light/20 dark:text-primary-light">
                      {activeItems.length}
                    </span>
                  </button>
                  <div className="mt-3 max-h-[62vh] space-y-2 overflow-y-auto pr-1">
                    {hourBucketList.map((bucket) => {
                      const selected = selectedHourKey === bucket.key
                      return (
                        <button
                          key={bucket.key}
                          type="button"
                          onClick={() => setSelectedHourKey(bucket.key)}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                            selected
                              ? 'border-primary bg-primary text-white shadow-md'
                              : 'border-transparent bg-white/50 text-gray-700 hover:bg-white hover:shadow-sm dark:bg-gray-800/35 dark:text-gray-200 dark:hover:bg-gray-800'
                          )}
                        >
                          <span
                            className={cn(
                              'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                              selected ? 'bg-white/20' : 'bg-primary/10 text-primary dark:bg-primary-light/10 dark:text-primary-light'
                            )}
                          >
                            <Clock className="h-4 w-4" aria-hidden />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-base font-bold leading-none">
                              {formatHourLabel(bucket.startsAt)}
                            </span>
                            <span className={cn('mt-1 block text-xs font-medium', selected ? 'text-white/75' : 'text-gray-500 dark:text-gray-400')}>
                              {formatHourDateLabel(bucket.startsAt)}
                            </span>
                          </span>
                          <span
                            className={cn(
                              'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                              selected ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                            )}
                          >
                            {bucket.count}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </aside>

                <div className="min-w-0 space-y-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
                        Shift list
                      </p>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {visibleItems.length} {visibleItems.length === 1 ? 'shift' : 'shifts'}
                        {selectedHour
                          ? ` at ${formatHourLabel(selectedHour.startsAt)}`
                          : ' across all hours'}
                      </p>
                    </div>
                  </div>

                  {visibleItems.length === 0 ? (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 py-12 text-center dark:border-gray-700 dark:bg-gray-900">
                      <Clock className="mx-auto h-8 w-8 text-gray-400" aria-hidden />
                      <p className="mt-3 text-sm font-semibold text-gray-900 dark:text-gray-100">
                        No shifts in this hour
                      </p>
                    </div>
                  ) : (
                    Array.from(groupedItems.entries()).map(([hourKey, group]) => (
                      <section key={hourKey} className="space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
                          <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/15 text-primary dark:bg-primary-light/20 dark:text-primary-light">
                              <Clock className="h-4 w-4" aria-hidden />
                            </span>
                            <div>
                              <h3 className="text-base font-bold text-gray-950 dark:text-gray-100">
                                {formatHourLabel(group[0].startsAt)}
                              </h3>
                              <p className="mt-0.5 flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                                <MapPin className="h-3.5 w-3.5" aria-hidden />
                                {formatDateLabel(group[0].startsAt)}
                              </p>
                            </div>
                          </div>
                          <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-gray-700 shadow-sm dark:bg-gray-800 dark:text-gray-200">
                            {group.length} {group.length === 1 ? 'shift' : 'shifts'}
                          </span>
                        </div>
                        {renderCompanyGroups(hourKey, group)}
                      </section>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      <UpcomingShiftDrawer
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
        schedulePath={selectedItem ? schedulePath(selectedItem.assignment) : '/shifts'}
      />
    </PageShell>
  )
}

function UpcomingShiftDrawer({
  item,
  onClose,
  schedulePath,
}: {
  item: UpcomingShiftItem | null
  onClose: () => void
  schedulePath: string
}): React.ReactElement | null {
  const navigate = useNavigate()
  if (!item) return null
  const color = getCompanyColor(item.assignment.companyId ?? item.companyName)
  const timeLabel =
    item.startTime && item.endTime
      ? `${item.startTime} - ${item.endTime}`
      : format(item.startsAt, 'p')

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 bg-black/35 backdrop-blur-sm"
        aria-label="Close shift details"
        onClick={onClose}
      />
      <aside className="absolute inset-y-0 right-0 flex w-full max-w-[460px] flex-col border-l border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary dark:text-primary-light">
              Shift details
            </p>
            <h2 className="mt-1 text-lg font-semibold text-gray-950 dark:text-gray-100">
              {item.shiftName}
            </h2>
          </div>
          <Button variant="ghost" className="p-2" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" aria-hidden />
          </Button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 dark:border-primary-light/30 dark:bg-primary-light/20">
            <div className="flex items-center gap-3">
              <Avatar name={item.userName} size="lg" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-950 dark:text-gray-100">
                  {item.userName}
                </p>
                {item.userEmail ? (
                  <p className="truncate text-xs text-gray-500 dark:text-gray-400">{item.userEmail}</p>
                ) : null}
              </div>
              <Badge variant={statusVariant(item.assignment.status)} className="ml-auto capitalize">
                {item.assignment.status}
              </Badge>
            </div>
          </div>

          <div className="grid gap-3">
            <DetailCard icon={Clock} label="Time" value={`${format(item.startsAt, 'PP')} · ${timeLabel}`} />
            <DetailCard icon={Building2} label="Company" value={item.companyName} dotClassName={color.dotBg} />
            <DetailCard icon={CalendarClock} label="Starts" value={format(item.startsAt, 'p')} />
            {item.endsAt ? <DetailCard icon={Clock} label="Ends" value={format(item.endsAt, 'PP p')} /> : null}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-950">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
              Notes
            </p>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              {item.assignment.notes || 'No notes added.'}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-950/50">
          <Button variant="secondary" onClick={onClose}>Close</Button>
          <Button className="gap-1.5" onClick={() => navigate(schedulePath)}>
            Open in schedule
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>
      </aside>
    </div>
  )
}

function DetailCard({
  icon: Icon,
  label,
  value,
  dotClassName,
}: {
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>
  label: string
  value: string
  dotClassName?: string
}): React.ReactElement {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-950">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
        <Icon className="h-3.5 w-3.5" aria-hidden />
        {label}
      </div>
      <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        {dotClassName ? <span className={cn('h-2 w-2 rounded-full', dotClassName)} aria-hidden /> : null}
        {value}
      </p>
    </div>
  )
}

UpcomingShiftsPage.displayName = 'UpcomingShiftsPage'
